(() => {
  'use strict';

  const onReady = (fn) => document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', fn, { once: true })
    : fn();

  onReady(() => {
    const closeNavigation = (current = null) => {
      document.querySelectorAll('[data-nav-item].is-open').forEach((item) => {
        if (current && (item === current || item.contains(current))) return;
        item.classList.remove('is-open');
        item.querySelector(':scope > .mp-nav-link-line > [data-submenu-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    };

    document.querySelectorAll('[data-submenu-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const item = toggle.closest('[data-nav-item]');
        if (!item) return;
        const willOpen = !item.classList.contains('is-open');
        closeNavigation(item);
        item.classList.toggle('is-open', willOpen);
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.mp-nav')) closeNavigation();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeNavigation();
        document.querySelector('.mp-nav [data-submenu-toggle][aria-expanded="true"]')?.focus();
      }
    });

    document.querySelectorAll('[data-localization-select]').forEach((select) => {
      select.addEventListener('change', () => {
        if (select.form) select.form.submit();
      });
    });

    document.addEventListener('click', (event) => {
      document.querySelectorAll('[data-localization-details][open]').forEach((details) => {
        if (!details.contains(event.target)) details.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-localization-details][open]').forEach((details) => {
        details.removeAttribute('open');
        details.querySelector('summary')?.focus();
      });
    });

    document.querySelectorAll('[data-search-overlay]').forEach((overlay) => {
      const openButtons = document.querySelectorAll(`[data-search-open][aria-controls="${overlay.id}"]`);
      const closeButtons = overlay.querySelectorAll('[data-search-close]');
      const input = overlay.querySelector('[data-search-input]');
      let lastFocus = null;

      const focusable = () => Array.from(overlay.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((node) => !node.hidden);
      const openSearch = () => {
        lastFocus = document.activeElement;
        overlay.hidden = false;
        overlay.setAttribute('aria-hidden', 'false');
        openButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));
        document.documentElement.classList.add('mp-search-is-open');
        window.requestAnimationFrame(() => input?.focus());
      };
      const closeSearch = () => {
        overlay.hidden = true;
        overlay.setAttribute('aria-hidden', 'true');
        openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
        document.documentElement.classList.remove('mp-search-is-open');
        if (lastFocus instanceof HTMLElement) lastFocus.focus();
      };

      openButtons.forEach((button) => button.addEventListener('click', openSearch));
      closeButtons.forEach((button) => button.addEventListener('click', closeSearch));
      overlay.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeSearch();
          return;
        }
        if (event.key !== 'Tab') return;
        const nodes = focusable();
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });

    document.querySelectorAll('[data-quantity]').forEach((control) => {
      const input = control.querySelector('input[type="number"]');
      if (!input) return;
      control.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        const min = Number(input.min || 0);
        input.value = Math.max(min, Number(input.value || 0) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      control.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        const max = input.max ? Number(input.max) : Infinity;
        input.value = Math.min(max, Number(input.value || 0) + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-gallery-main]');
      if (!main) return;
      gallery.querySelectorAll('[data-gallery-thumb][data-media-type="image"]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (!thumb.dataset.full) return;
          main.style.setProperty('--mp-product-image', `url(\"${thumb.dataset.full.replace(/\"/g, '%22')}\")`);
          main.classList.add('mp-product-gallery__main--image');
          main.classList.remove('mp-product-gallery__main--media');
          main.setAttribute('role', 'img');
          main.setAttribute('aria-label', thumb.dataset.alt || 'Product image');
          main.querySelectorAll('video, iframe, model-viewer, .shopify-model-viewer-ui').forEach((node) => node.remove());
          gallery.querySelectorAll('[data-gallery-thumb]').forEach((node) => node.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    });

    document.querySelectorAll('[data-product-root]').forEach((root) => {
      const select = root.querySelector('[data-variant-select]');
      if (!select) return;
      const price = root.querySelector('[data-product-price] strong, [data-product-price] .mp-price-quote');
      const sku = root.querySelector('[data-product-sku]');
      const addButton = root.querySelector('[data-add-to-cart]');
      const addLabel = root.querySelector('[data-add-label]');
      const quoteButton = root.querySelector('[data-quote-button]');
      const storePayButtons = root.querySelectorAll('[data-store-app-direct-pay]');
      const zeroAsQuote = document.body.dataset.zeroPriceAsQuote === 'true';

      const updateVariant = () => {
        const option = select.options[select.selectedIndex];
        const cents = Number(option.dataset.price || 0);
        const available = option.dataset.available === 'true';
        const quote = zeroAsQuote && cents <= 0;
        if (price) price.textContent = quote ? 'Request a quote' : option.dataset.priceFormatted;
        if (sku) sku.textContent = option.dataset.sku || '';
        if (addButton) {
          addButton.classList.toggle('d-none', quote);
          addButton.disabled = !available;
        }
        if (addLabel) addLabel.textContent = available ? 'Add to cart' : 'Sold out';
        storePayButtons.forEach((button) => {
          button.classList.toggle('d-none', quote);
          button.disabled = !available;
        });
        if (quoteButton) {
          quoteButton.classList.toggle('d-none', !quote);
          const url = new URL(quoteButton.href, window.location.origin);
          url.searchParams.set('variant', option.value);
          if (option.dataset.sku) url.searchParams.set('product', option.dataset.sku);
          quoteButton.href = url.toString();
        }
      };
      select.addEventListener('change', updateVariant);
    });

    const checkoutEnabled = document.body.dataset.storeAppCheckoutEnabled === 'true';
    const routeRoot = window.Shopify?.routes?.root || '/';
    const route = (path) => `${routeRoot}${path}`.replace(/\/{2,}/g, '/');
    const modal = document.querySelector('[data-payment-choice]');
    const modalDialog = modal?.querySelector('.mp-payment-choice__dialog');
    const modalTitle = modal?.querySelector('[data-payment-choice-title]');
    const modalCopy = modal?.querySelector('[data-payment-choice-copy]');
    const modalStatus = modal?.querySelector('[data-payment-choice-status]');
    let modalReturnFocus = null;

    const updateCartCount = (cart) => {
      document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = String(cart?.item_count || 0); });
    };

    const getCart = async () => {
      const response = await fetch(`${route('cart.js')}?t=${Date.now()}`, {
        headers: { 'Accept': 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('The Shopify cart could not be loaded.');
      return response.json();
    };

    const addProductForm = async (form) => {
      const response = await fetch(route('cart/add.js'), {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(form),
        credentials: 'same-origin'
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this product.');
      const cart = await getCart();
      updateCartCount(cart);
      return cart;
    };

    const syncCartForm = async () => {
      const form = document.querySelector('.mp-cart-page form[action*="/cart"]');
      if (!form) return;
      const data = new FormData(form);
      const response = await fetch(route('cart/update.js'), {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: data,
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Cart quantities could not be updated.');
      updateCartCount(await response.json());
    };

    const openPaymentChoice = (mode = 'checkout', trigger = null) => {
      if (!modal) return;
      modalReturnFocus = trigger || document.activeElement;
      if (modalTitle) modalTitle.textContent = mode === 'added' ? 'Added to your cart' : 'How would you like to pay?';
      if (modalCopy) modalCopy.textContent = mode === 'added'
        ? 'Continue shopping, use Shopify checkout, or pay through the Master Palm Official Store.'
        : 'Choose Shopify checkout or complete payment securely through the Master Palm Official Store.';
      if (modalStatus) modalStatus.textContent = '';
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('mp-payment-choice-open');
      window.requestAnimationFrame(() => modalDialog?.focus());
    };

    const closePaymentChoice = () => {
      if (!modal) return;
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('mp-payment-choice-open');
      if (modalReturnFocus instanceof HTMLElement) modalReturnFocus.focus();
    };

    const transferCart = async (payment) => {
      if (!checkoutEnabled) throw new Error('Master Palm Store checkout is not enabled.');
      const endpoint = String(document.body.dataset.storeAppCheckoutUrl || '').trim();
      if (!/^https:\/\//i.test(endpoint)) throw new Error('The Master Palm Store checkout URL is not configured.');
      if (modalStatus) modalStatus.textContent = 'Preparing your secure checkout…';
      await syncCartForm();
      const cart = await getCart();
      if (!Array.isArray(cart.items) || cart.items.length < 1) throw new Error('Your cart is empty.');
      const missingSku = cart.items.find((item) => !String(item.sku || '').trim());
      if (missingSku) throw new Error(`A SKU is required before ${missingSku.product_title || 'this product'} can be transferred.`);

      const transfer = document.createElement('form');
      transfer.method = 'post';
      transfer.action = endpoint;
      transfer.style.display = 'none';
      const fields = {
        payment,
        cart_json: JSON.stringify(cart),
        shop_domain: String(window.Shopify?.shop || ''),
        storefront_domain: window.location.hostname,
        return_url: `${window.location.origin}${route('cart')}`,
        source_url: window.location.href,
        currency: String(cart.currency || window.Shopify?.currency?.active || ''),
        locale: document.documentElement.lang || 'en'
      };
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        transfer.appendChild(input);
      });
      document.body.appendChild(transfer);
      transfer.submit();
    };

    document.querySelectorAll('.mp-product-form').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        if (!checkoutEnabled || !window.fetch) return;
        event.preventDefault();
        const button = form.querySelector('[data-add-to-cart]');
        button?.setAttribute('disabled', 'disabled');
        try {
          await addProductForm(form);
          openPaymentChoice('added', button);
        } catch (error) {
          if (modalStatus) modalStatus.textContent = error.message || 'Unable to add this product.';
          window.alert(error.message || 'Unable to add this product.');
        } finally {
          button?.removeAttribute('disabled');
        }
      });
    });

    document.querySelectorAll('.mp-card-product-form').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        if (!window.fetch) return;
        event.preventDefault();
        const button = form.querySelector('button');
        button?.setAttribute('disabled', 'disabled');
        try {
          await addProductForm(form);
          if (button) button.innerHTML = '<span>Added</span>';
          if (checkoutEnabled) openPaymentChoice('added', button);
          setTimeout(() => { if (button) button.innerHTML = '<span>Add to cart</span>'; }, 1600);
        } catch (error) {
          form.submit();
        } finally {
          button?.removeAttribute('disabled');
        }
      });
    });

    document.querySelectorAll('[data-store-app-direct-pay]').forEach((button) => {
      button.addEventListener('click', async () => {
        button.disabled = true;
        try {
          const formId = button.dataset.productFormId;
          if (formId) {
            const productForm = document.getElementById(formId);
            if (!productForm) throw new Error('The product selection form could not be found.');
            await addProductForm(productForm);
          }
          await transferCart(button.dataset.storeAppDirectPay || 'paypal');
        } catch (error) {
          button.disabled = false;
          window.alert(error.message || 'Secure checkout could not be started.');
        }
      });
    });

    document.querySelectorAll('[data-payment-choice-open]').forEach((button) => {
      button.addEventListener('click', () => openPaymentChoice(button.dataset.paymentChoiceOpen || 'checkout', button));
    });
    modal?.querySelectorAll('[data-payment-choice-close]').forEach((button) => button.addEventListener('click', closePaymentChoice));
    modal?.querySelector('[data-shopify-checkout]')?.addEventListener('click', async () => {
      try {
        if (modalStatus) modalStatus.textContent = 'Updating your Shopify cart…';
        await syncCartForm();
        window.location.assign(route('checkout'));
      } catch (error) {
        if (modalStatus) modalStatus.textContent = error.message || 'Shopify checkout could not be opened.';
      }
    });
    modal?.querySelectorAll('[data-store-app-payment]').forEach((button) => {
      button.addEventListener('click', async () => {
        modal?.querySelectorAll('button').forEach((node) => { node.disabled = true; });
        try {
          await transferCart(button.dataset.storeAppPayment || 'paypal');
        } catch (error) {
          modal?.querySelectorAll('button').forEach((node) => { node.disabled = false; });
          if (modalStatus) modalStatus.textContent = error.message || 'Secure checkout could not be started.';
        }
      });
    });
    modal?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePaymentChoice();
      }
    });
  });
})();
